import { assert } from "@ember/debug";
import ControlTower from "wise-control-tower";

interface Molecule {
  code: string;
  label: string;
}

export interface MedicationPayload {
  cisCode?: string;
  name?: string;
  uid?: string;
  dose?: string;
  unit?: string;
  galenic?: string;
  shortDescription?: string;
  longDescription?: string;
  brand?: string;
  state?: string;
  molecule?: Molecule;
}

/**
 * Medication represents two things:
 * - a medication from the "Medication database" (the "medications.js" files),
 * - a medication stored in a JE.
 *
 * This is represented by the two interfaces MedicationJSONPayload and MedicationPayload.
 * This should probably be split into two different models.
 *
 * Something to keep in mind when refactoring this part when the medication comes from the back.
 */
export default class Medication implements MedicationPayload {
  brand?: string;
  cisCode?: string;
  dose?: string;
  galenic?: string;
  longDescription?: string;
  molecule?: Molecule;
  name?: string;
  shortDescription?: string;
  state?: string;
  uid?: string;
  unit?: string;

  get description(): string {
    return (this.shortDescription || this.name || this.cisCode) as string;
  }

  /**
   * This is the name displayed on the dashboard
   */
  get displayName(): string {
    return (this.brand || this.name) as string;
  }

  /**
   * Returns true if the Medication contains two molecules within the same ATC code.
   * For example, Medication with cisCode code 65181349 and uid Sulfamethoxazole_400_mg_+_Trimethoprime_80_mg_comprime_BACTRIM
   * has two molecules, then, two dosages and units : 400-80 and mg-mg.
   * It's the only current way to know that.
   */
  get hasSeveralMolecules(): boolean {
    return (this.dose?.includes("-") && this.unit?.includes("-")) || false;
  }

  get jsonPayload(): MedicationPayload {
    return {
      cisCode: this.cisCode,
      uid: this.uid,
      dose: this.dose,
      unit: this.unit,
      galenic: this.galenic,
      shortDescription: this.shortDescription,
      longDescription: this.longDescription,
      name: this.name,
      brand: this.brand,
      state: this.state,
      molecule: this.molecule
    };
  }

  constructor(payload: MedicationPayload) {
    const { cisCode } = payload;

    if (cisCode && cisCode.startsWith("CIS-")) {
      assert(
        `The CIS code must have 8 digits : ${cisCode}`,
        cisCode.length === 12
      );
    }

    for (const key in payload) {
      if (payload[key]) {
        this[key] = payload[key];
      }
    }

    // Have to add it there because `molecule` is a getter on the `MedicationSpecialty` model
    this.molecule = payload.molecule;
  }

  isEqual(other: Medication | undefined): boolean {
    if (!this.cisCode && !this.name)
      ControlTower.captureException(
        new Error(
          `[Medication] - Failure to compare drugs due to lack of data, ${JSON.stringify(this)}`
        )
      );
    return (
      this === other ||
      Boolean(this.cisCode && this.cisCode === other?.cisCode) ||
      Boolean(this.name && this.name === other?.name)
    );
  }
}
